'use client';

import { Fragment } from 'react';

import { useTranslations } from '@/components/language-provider';

type PolicySubSection = {
  subheading: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
  definitions?: readonly { term: string; description: string }[];
  footnote?: string;
};

type PolicySection = {
  heading: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
  subSections?: readonly PolicySubSection[];
};

export function PrivacyPolicyContent() {
  const { messages } = useTranslations();
  const policy = messages.privacyPolicy;

  return (
    <article className="space-y-12">
      <header className="rounded-3xl border border-border/60 bg-gradient-to-br from-primary/10 via-surface to-surface/90 p-8 shadow-xl shadow-primary/10 backdrop-blur-md">
        <div className="space-y-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">{policy.title}</p>
          <h1 className="text-3xl font-bold text-foreground sm:text-4xl">{policy.title}</h1>
          <p className="text-sm text-muted-foreground">{policy.lastUpdated}</p>
          <div className="space-y-3 text-base text-muted-foreground sm:text-lg">
            {policy.intro.map((paragraph: string, index: number) => (
              <p key={`intro-${index}`}>{paragraph}</p>
            ))}
          </div>
        </div>
      </header>

      <div className="space-y-10">
        {policy.sections.map((section: PolicySection, sectionIndex: number) => (
          <section
            key={`section-${sectionIndex}`}
            aria-labelledby={`section-${sectionIndex}-title`}
            className="space-y-6 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-md shadow-primary/5"
          >
            <div className="space-y-3">
              <h2 id={`section-${sectionIndex}-title`} className="text-2xl font-semibold text-foreground">
                {section.heading}
              </h2>
              {section.paragraphs?.map((paragraph, paragraphIndex) => (
                <p key={`section-${sectionIndex}-paragraph-${paragraphIndex}`} className="text-base text-muted-foreground">
                  {paragraph}
                </p>
              ))}
            </div>

            {section.bullets && (
              <ul className="space-y-2 rounded-2xl border border-border/50 bg-muted/30 p-5 text-sm text-muted-foreground">
                {section.bullets.map((item, itemIndex) => (
                  <li key={`section-${sectionIndex}-bullet-${itemIndex}`} className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 rounded-full bg-primary" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.subSections?.map((subSection, subIndex) => (
              <Fragment key={`section-${sectionIndex}-subsection-${subIndex}`}>
                <div className="space-y-4 rounded-2xl border border-border/50 bg-surface p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground">{subSection.subheading}</h3>
                  {subSection.paragraphs?.map((paragraph, paragraphIndex) => (
                    <p
                      key={`section-${sectionIndex}-subsection-${subIndex}-paragraph-${paragraphIndex}`}
                      className="text-sm text-muted-foreground"
                    >
                      {paragraph}
                    </p>
                  ))}
                  {subSection.bullets && (
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {subSection.bullets.map((item, itemIndex) => (
                        <li
                          key={`section-${sectionIndex}-subsection-${subIndex}-bullet-${itemIndex}`}
                          className="flex items-start gap-3 rounded-xl bg-muted/30 px-3 py-2"
                        >
                          <span className="mt-1 size-1.5 rounded-full bg-primary" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {subSection.definitions && (
                    <dl className="grid gap-4 sm:grid-cols-2">
                      {subSection.definitions.map((definition, definitionIndex) => (
                        <div
                          key={`section-${sectionIndex}-subsection-${subIndex}-definition-${definitionIndex}`}
                          className="rounded-xl border border-border/40 bg-muted/20 p-4 text-sm"
                        >
                          <dt className="font-semibold text-foreground">{definition.term}</dt>
                          <dd className="mt-1 text-muted-foreground">{definition.description}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {subSection.footnote && (
                    <p className="text-xs font-medium text-muted-foreground/80">{subSection.footnote}</p>
                  )}
                </div>
              </Fragment>
            ))}
          </section>
        ))}
      </div>

      <section className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/90 to-primary/70 p-8 text-primary-foreground shadow-lg shadow-primary/40">
        <h2 className="text-2xl font-semibold">{policy.contact.heading}</h2>
        <div className="mt-4 space-y-3 text-sm sm:text-base">
          {policy.contact.paragraphs.map((paragraph: string, index: number) => (
            <p key={`contact-paragraph-${index}`}>{paragraph}</p>
          ))}
        </div>
        <ul className="mt-5 space-y-2 text-sm sm:text-base">
          {policy.contact.entries.map((entry: { label: string; value: string }, entryIndex: number) => (
            <li
              key={`contact-entry-${entryIndex}`}
              className="flex flex-col gap-1 rounded-xl bg-primary-foreground/10 px-4 py-3 text-primary-foreground sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="font-semibold uppercase tracking-wide">{entry.label}</span>
              <span className="font-medium">{entry.value}</span>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
}
